import {
	Injectable,
	Injector,
	ComponentRef,
	ApplicationRef,
	Renderer2,
	RendererFactory2,
	Type,
	EventEmitter,
	createComponent
} from "@angular/core";
import { BsModalService } from "ngx-bootstrap/modal";

@Injectable({
	providedIn: "root"
})
export class AtlasModalService {
	private componenteRef: ComponentRef<unknown> | null = null;
	private readonly renderer: Renderer2;

	onShowed = new EventEmitter();
	onHidden = new EventEmitter();

	constructor(
		private injector: Injector,
		private appRef: ApplicationRef,
		private rendererFactory: RendererFactory2,
		private bsModalService: BsModalService
	) {
		this.renderer = this.rendererFactory.createRenderer(null, null);
	}

	openModal<ComponentT, StateT = Record<string, unknown>>(
		component: Type<ComponentT>,
		config: { initialState?: Partial<StateT> } = {}
	): ComponentRef<ComponentT> {
		const hasLegacyModalOpen = this.bsModalService.getModalsCount() > 0;

		if (hasLegacyModalOpen) {
			return this.componenteRef as ComponentRef<ComponentT>;
		}

		if (this.componenteRef) {
			this.closeModal(this.componenteRef);
		}

		this.componenteRef = createComponent(component, {
			environmentInjector: this.appRef.injector
		});

		const initialState: object = config.initialState ?? {};
		if (this.componenteRef) {
			this._assignInitialState(this.componenteRef, initialState);
			this._attachView(this.componenteRef);
		}

		return this.componenteRef as ComponentRef<ComponentT>;
	}

	closeModal(componentRef?: ComponentRef<unknown> | null): void {
		const ref = componentRef ?? this.componenteRef;
		if (ref) {
			this._detachView(ref);
		}
	}

	private _assignInitialState(
		componentRef: ComponentRef<unknown>,
		initialState: Partial<Record<string, unknown>> = {}
	): void {
		Object.assign(
			componentRef.instance as Record<string, unknown>,
			initialState
		);
	}

	private _attachView(componentRef: ComponentRef<unknown>): void {
		this.appRef.attachView(componentRef.hostView);
		const domElement = componentRef.location.nativeElement as HTMLElement;
		this.renderer.appendChild(document.body, domElement);
		this.onShowed.emit(componentRef.instance);
	}

	private _detachView(componentRef: ComponentRef<unknown>): void {
		if (componentRef) {
			if (componentRef.hostView) {
				this.appRef.detachView(componentRef.hostView);
			}
			this.onHidden.emit(componentRef.instance);
			componentRef.destroy();
			if (this.componenteRef === componentRef) {
				this.componenteRef = null;
			}
		}
	}
}
